// src/pages/Reports/IssuanceReporting/IssuanceTicketLookup/RangeLookupTab.tsx
// RPT-016 (README.md mục 7) — tra cứu 1 dải vé, trả về danh sách các đoạn (segment) trạng thái
// khác nhau chồng lên dải người dùng nhập. Không phân trang/export — bảng hiển thị đơn giản.

import React, { useMemo, useState } from 'react';
import { Alert, Button, Col, Form, Input, Label, Row, Spinner } from 'reactstrap';
import { toast } from 'react-toastify';

import DataTableShell from 'Components/Common/DataTableShell';
import { lookupTicketRangeReportApi } from 'helpers/fakebackend_helper';
import { buildAgentMap, getAgentCode, getAgentName, loadAgentsWithCache } from 'helpers/agentLookup';
import { IAgent } from 'slices/agentManagement/types';
import IssuancePlanSelect from '../shared/IssuancePlanSelect';
import { formatDateTime, formatQty } from '../shared/formatters';
import { TicketRangeLookupResult } from '../shared/types';
import { createScreenText } from 'helpers/screenText';
const st = createScreenText('reports_issuance_reporting');

const STATE_BADGE: Record<string, string> = {
  ConKho: 'success',
  DaGiao: 'primary',
  DaThuHoi: 'warning',
};

const RangeLookupTab: React.FC = () => {
  const [planId, setPlanId] = useState('');
  const [symbol, setSymbol] = useState('');
  const [rangeStart, setRangeStart] = useState('');
  const [rangeEnd, setRangeEnd] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<TicketRangeLookupResult | null>(null);
  const [agents, setAgents] = useState<IAgent[]>([]);
  const agentMap = useMemo(() => buildAgentMap(agents), [agents]);

  const canSubmit = !!planId && !!symbol.trim() && !!rangeStart.trim() && !!rangeEnd.trim();

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!canSubmit) return;
    setLoading(true);
    setResult(null);
    try {
      if (agents.length === 0) loadAgentsWithCache().then(setAgents);
      const res = await lookupTicketRangeReportApi({
        IssuancePlanId: planId,
        Symbol: symbol.trim(),
        RangeStart: rangeStart.trim(),
        RangeEnd: rangeEnd.trim(),
      });
      setResult((res as any)?.data ?? res);
    } catch (error: any) {
      toast.error(error?.message || st('khong_the_tra_cuu_dai_ve_message'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Form onSubmit={handleSubmit}>
        <Row className="g-3 mb-3 align-items-end">
          <Col md={3}>
            <Label className="form-label">{st('ky_phat_hanh_label')} <span className="text-danger">*</span></Label>
            <IssuancePlanSelect requireSelection value={planId} onChange={setPlanId} />
          </Col>
          <Col md={2}>
            <Label className="form-label">{st('ky_hieu_label')} <span className="text-danger">*</span></Label>
            <Input required type="text" placeholder={st('vd_n_placeholder')} value={symbol} onChange={(e) => setSymbol(e.target.value)} />
          </Col>
          <Col md={2}>
            <Label className="form-label">{st('tu_so_column')} <span className="text-danger">*</span></Label>
            <Input required type="text" placeholder="000001" value={rangeStart} onChange={(e) => setRangeStart(e.target.value)} />
          </Col>
          <Col md={2}>
            <Label className="form-label">{st('den_so_column')} <span className="text-danger">*</span></Label>
            <Input required type="text" placeholder="000100" value={rangeEnd} onChange={(e) => setRangeEnd(e.target.value)} />
          </Col>
          <Col md={3}>
            <Button color="primary" type="submit" disabled={!canSubmit || loading} className="w-100">
              {loading ? <Spinner size="sm" className="me-1" /> : <i className="ri-search-line align-bottom me-1"></i>}
              {st('tra_cuu_button')}
            </Button>
          </Col>
        </Row>
      </Form>

      {loading ? (
        <div className="text-center py-5"><Spinner color="primary" /></div>
      ) : result ? (
        <>
          <div className="mb-3">
            <strong>{result.planCode}</strong> {st('ky_hieu_text')} {result.symbol}{st('dai_text')} {result.rangeStart} - {result.rangeEnd}
            {' '}{st('tong_text')} {formatQty(result.totalQuantity)} {st('ve_text')}
          </div>
          <DataTableShell
            headerRow={
              <tr>
                <th>{st('tu_so_column')}</th>
                <th>{st('den_so_column')}</th>
                <th className="text-end">{st('sl_column')}</th>
                <th>{st('trang_thai_column')}</th>
                <th>{st('dai_ly_label')}</th>
                <th>{st('ma_phieu_column')}</th>
                <th>{st('thoi_diem_column')}</th>
              </tr>
            }
          >
            {result.segments.length === 0 ? (
              <tr><td colSpan={7} className="text-center text-muted py-5">{st('khong_co_du_lieu_text')}</td></tr>
            ) : (
              result.segments.map((seg, index) => (
                <tr key={`${seg.rangeStart}-${index}`}>
                  <td>{seg.rangeStart}</td>
                  <td>{seg.rangeEnd}</td>
                  <td className="text-end">{formatQty(seg.quantity)}</td>
                  <td>
                    <span className={`badge bg-${STATE_BADGE[seg.currentState] || 'secondary'}-subtle text-${STATE_BADGE[seg.currentState] || 'secondary'}`}>
                      {seg.currentStateName}
                    </span>
                  </td>
                  <td>{seg.agentId != null ? `${getAgentName(agentMap, seg.agentId)} (${getAgentCode(agentMap, seg.agentId)})` : '-'}</td>
                  <td>{seg.referenceCode || '-'}</td>
                  <td>{formatDateTime(seg.eventAt)}</td>
                </tr>
              ))
            )}
          </DataTableShell>
        </>
      ) : (
        <Alert color="info" className="mb-0">{st('nhap_du_thong_tin_va_bam_message')}</Alert>
      )}
    </>
  );
};

export default RangeLookupTab;
